import { UserService } from '../../dashboard/program-list/user.service';
import { ProgramService } from '../../shared/services/program.service';
import { ErrorService } from './../../shared/services/error.service';

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';

import { User } from '../../models/program/user';
import { UserProfile } from '../../models/program/user-profile';
import { UserGroup } from '../../models/program/user-group';
import { Program } from '../../models/program/program';
import { CurrentUserService } from '../services/current-user.service';
import { SomePermissions } from '../../models/permissions';
import { PermissionsService } from '../services/permissions.service';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.less']
})

export class UserSettingsComponent implements OnInit {
  @Input() user: User;
  @Output() recall: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() navigated: EventEmitter<void> = new EventEmitter<void>();
  public userInfo: any;
  public avatar: string;
  public settings: FormGroup;
  public settingsProfile: FormArray;
  public settingsGroups: FormArray;
  public pass: string;
  public confirm: string;
  public passWarn = false;
  public saving: boolean;
  public saved: boolean;
  public selectedGroupId: {groupIndex: number, programIndex: number} = null;
  public tab = 'profile'; // which of the profile/teams tabs is active
  public programs: Program[] = []; // Array of programs for the new team selector
  public errors: string[];
  public permissions: SomePermissions = {};
  public impersonateDialogueVisible: boolean;
  public deleteDialogueVisible: boolean;
  public deleting: boolean;
  public deleteErrors: string[];
  public connectSsoDialogueVisible: boolean;
  public connectingSso: boolean;
  public connectSsoErrors: string[];

  constructor(
    public currentUserService: CurrentUserService,
    private userService: UserService,
    private _fb: FormBuilder,
    private programService: ProgramService,
    private permissionsService: PermissionsService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    this.permissionsService.retrievePermissions().subscribe(permissions => {
      this.permissions = permissions;
    });

    this.settings = this._fb.group({
      first_name: [this.user.first_name, <any>Validators.required],
      last_name: [this.user.last_name, <any>Validators.required],
      email: [this.user.email, <any>Validators.required],
      avatar: [this.user.avatar],
      active: [this.user.active],
      require_payment: [this.user.require_payment],
      community_expert: [this.user.community_expert],
      disable_all_emails: [this.user.disable_all_emails],
      potential_admin: [this.user.potential_admin],
      edition_admin: [this.user.edition_admin],
      edition_admin_analytics: [this.user.edition_admin_analytics],
      comment_moderator: [this.user.comment_moderator],
      resource_editor: [this.user.resource_editor],
      profile: this._fb.array([]),
      password: [null, [<any>Validators.required, <any>Validators.minLength(1)]], // default to something invalid
      groups: this._fb.array([]),
    });
    this.settingsProfile = this.settings.get('profile') as FormArray;
    this.settingsGroups = this.settings.get('groups') as FormArray;

    this.settings.controls['password'].disable(); // disable so it doesn't get validated or submitted

    this.initProfiles(this.user.profile);
    this.initGroups(this.user.groups);

    console.log(this.user, this.settings.value);

    // Fetch the list of programs needed for adding the user to new groups.
    this.programService.getPrograms().subscribe(programs => this.programs = programs);
  }

  /**
   * Public Methods
   */

  public save(model: any, isValid: boolean): void {
    console.log(model, isValid);
    this.saving = true;
    this.saved = false;
    this.errors = [];
    this.userService.saveUserSettings(this.user.id, model)
                    .subscribe(res => {
                      this.saving = false;
                      this.saved = true;
                      this.settings.markAsPristine();
                      this.recall.emit(true);
                    }, errorResponse => {
                      this.errors = this.errorService.messages(errorResponse);
                      this.saving = false;
                    });
  }

  public checkPass(pass: string, confirm: string): void {
    const passwordSetting = this.settings.controls['password'];
    if (confirm === pass) {
      this.passWarn = false;
      passwordSetting.patchValue(pass);
    } else {
      this.passWarn = true;
      passwordSetting.patchValue(null); // something that will fail the client side validation
    }

    if (pass || confirm) {
      passwordSetting.enable();
    } else {
      passwordSetting.disable(); // ignore and don't send if empty
    }
  }

  /**
   * Private Methods
   */

  private initProfiles(profiles: UserProfile[]): any {
    const control: FormArray = <FormArray>this.settings.controls['profile'];
    profiles.forEach(profile => control.push(this.profileForm(profile)));
  }


  private profileForm(profile: { field_id: number, value: string }): FormGroup {
    let profileForm: FormGroup = this._fb.group({
      field_id: [null],
      value: [null]
    });
    profileForm.patchValue(profile);
    return profileForm;
  }

  private initGroups(groups: UserGroup[]): any {
    const control: FormArray = <FormArray>this.settings.controls['groups'];
    groups.forEach(group => control.push(new FormControl(group['id'])));
  }

  public changeTab(event: Event, tabName: string) {
    this.tab = tabName;
    event.preventDefault();
  }

  private removeFromGroup(index: number) {
    const formArray: FormArray = <FormArray>this.settings.controls['groups'];
    // remove this group from the form array
    formArray.removeAt(index);
    // and also from the groups array on the user, as this is used to render
    // the contents of the group table
    this.user.groups.splice(index, 1);
  }

  public addToGroup(address: {groupIndex: number, programIndex: number}) {
    // get the proram and group list item that was chosen in the <select>
    const program = this.programs[address.programIndex]
    const groupItem = program.groups[address.groupIndex];

    // translate it into a user group object and append to the user's groups
    const userGroup = new UserGroup();
    userGroup.id = groupItem.id;
    userGroup.name = groupItem.name;
    userGroup.program_title = program.title;
    userGroup.progress = null;
    this.user.groups.push(userGroup);

    // add the group id to the form
    const formArray: FormArray = <FormArray>this.settings.controls['groups'];
    const formControl = new FormControl(groupItem.id);
    formArray.push(formControl);
  }

  public showImpersonateDialogue() {
    this.impersonateDialogueVisible = true;
    return false;
  }

  public showDeleteDialogue() {
    this.deleteDialogueVisible = true;
    return false;
  }

  public deleteUser() {
    if (this.deleting) {
      return;
    }
    this.deleteErrors = null;
    this.deleting = true;
    this.userService.deleteUser(this.user.id).subscribe(() => {
      this.deleting = false;
      this.recall.emit(true);
    }, errorResponse => {
      this.deleteErrors = this.errorService.messages(errorResponse);
      this.deleting = false;
    });
  }

  public get ssoSubscription(): any {
    const subscriptionWithSso = this.user.subscriptions.find(subscription => subscription.saml_providers);
    return subscriptionWithSso;
  }

  public connectSso() {
    if (this.connectingSso) {
      return;
    }
    this.connectSsoErrors = null;
    this.connectingSso = true;
    this.userService.connectSso(this.user.id, this.ssoSubscription.saml_providers[0].id).subscribe(() => {
      this.connectingSso = false;
      this.recall.emit(true);
    }, errorResponse => {
      this.connectSsoErrors = this.errorService.messages(errorResponse);
      this.connectingSso = false;
    });
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/shared/user-settings/user-settings.component.ts